import { emptyGuid } from "@insite/client-framework/Common/StringHelpers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Dictionary } from "@insite/client-framework/Common/Types";
import Logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getCategoriesDataView,
    getCategoryDepthLoaded,
    getCategoryState,
    getSubCategoryIds,
} from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategories from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategories";
import loadCategory from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategory";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
    categories = "categories",
}

export interface CategoryLinkFieldValue {
    type: "Category";
    value: string;
}
interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.categories]: LinkModel[];
    };
}

interface LinkModel {
    fields: {
        categoryLink: CategoryLinkFieldValue;
    };
}

export interface MappedLink {
    title: string;
    url: string;
    smallImagePath: string;
}

const getParameter = (categoryId: string, maxDepth: number) => ({
    maxDepth,
    startCategoryId: categoryId === emptyGuid ? undefined : categoryId,
    includeStartCategory: false,
});

const setupCategoryLink = (
    state: ApplicationState,
    value: string,
    categoryIdsToLoad: Dictionary<number>,
    maxDepth: number,
) => {
    let mappedLink: MappedLink | undefined;

    if (!value) {
        return;
    }

    if (value === emptyGuid) {
        mappedLink = {
            url: "",
            title: translate("Products"),
            smallImagePath: "",
        };
    } else {
        const categoryState = getCategoryState(state, value);
        if (!categoryState.value) {
            if (!categoryState.isLoading) {
                categoryIdsToLoad[value] = maxDepth;
            }
            return;
        }
        mappedLink = {
            url: categoryState.value.path,
            title: categoryState.value.shortDescription,
            smallImagePath: categoryState.value.smallImagePath,
        };
    }

    return mappedLink;
};

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const {
        fields: { categories, title },
    } = ownProps;
    const mappedLinks = [];
    const categoryIdsToLoad: Dictionary<number> = {};
    for (const category of categories) {
        const {
            fields: { categoryLink },
        } = category;
        const { type, value } = categoryLink;

        const depthToLoad = 2;
        const mappedLink: MappedLink | undefined = setupCategoryLink(state, value, categoryIdsToLoad, depthToLoad);
        if (!mappedLink) {
            continue;
        }
        mappedLinks.push(mappedLink);
    }
    return {
        title,
        categoryLinks: mappedLinks,
        categoryIdsToLoad,
    };
};

const mapDispatchToProps = { loadCategories };

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface IntPopularCategoriesStyles {
    container?: GridContainerProps;
    categoryItem?: GridItemProps;
    categoryImageLink?: LinkPresentationProps;
    categoryImage?: LazyImageProps;
    categoryNameLinkItem?: GridItemProps;
    categoryNameLink?: LinkPresentationProps;
    categoryName?: TypographyProps;
    titleText?: TypographyPresentationProps;
    categoryWraper?: InjectableCss;
    popularCategoriescontainer?: InjectableCss;
    categoryNameContainer?: InjectableCss;
}
export const intPopularCategoriesStyles: IntPopularCategoriesStyles = {
    popularCategoriescontainer: {
        css: css`
            padding: 30px 0px;
            max-width: 1140px;
            margin: 0 auto;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            max-width: 100% !important;
                            padding: 27px 0px;
                        `,
                        css`
                            max-width: 100% !important;
                            padding: 27px 0px;
                        `,
                        css`
                            max-width: 720px !important;
                            padding: 30px 0px;
                        `,
                        css`
                            max-width: 960px !important;
                            padding: 30px 0px;
                        `,
                        css`
                            max-width: 1140px !important;
                            padding: 30px 0px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    container: {
        gap: 38,
        css: css`
            align-items: center;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            max-width: 100% !important;
                            padding: 0 30px;
                        `,
                        css`
                            max-width: 100% !important;
                            padding: 0 30px;
                        `,
                        css`
                            max-width: 720px !important;
                            padding: 0 0px;
                        `,
                        css`
                            max-width: 960px !important;
                            padding: 0 0px;
                        `,
                        css`
                            max-width: 1140px !important;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    categoryWraper: {
        css: css`
            border: 1px solid #e5e5e5;
            width: 100%;
            padding: 0px 4px;
            display: flex;
            height: 97px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            height: auto;
                            flex-direction: column;
                            align-items: center;
                        `,
                        null,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    categoryItem: {
        width: [12, 12, 6, 4, 4],
        css: css`
            align-items: center;
            padding-bottom: 9px;
            margin: 0 auto;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding-bottom: 0px;
                            padding-top: 10px;
                        `,
                        css`
                            padding-bottom: 0px;
                            padding-top: 10px;
                        `,
                        css`
                            padding-bottom: 9px;
                            padding-top: 19px;
                        `,
                        css`
                            padding-bottom: 9px;
                            padding-top: 19px;
                        `,
                        css`
                            padding-bottom: 9px;
                            padding-top: 19px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    categoryImage: {
        width: "120px",
        height: "86px",
        css: css`
            img {
                width: auto !important;
                height: 100% !important;
            }
        `,
    },
    categoryNameLinkItem: {
        width: 12,
        css: css`
            padding-bottom: 8px;
        `,
    },
    categoryName: {
        css: css`
            color: #33333a;
            font-size: 21px;
            font-weight: 600;
            letter-spacing: 0;
            line-height: 22px;
            &:hover {
                color: #006bc0;
            }
        `,
    },
    categoryNameContainer: {
        css: css`
            color: #33333a;
            margin-left: 16px;
            @media (max-width: 575px) {
                margin-left: 0;
            }
        `,
    },
    titleText: {
        variant: "h2",
        css: css`
            text-align: center;
            margin-bottom: 28px;
            font-size: 30px;
            font-weight: 500;
            letter-spacing: 0;
            line-height: 36px;
            text-align: center;
            color: #33333a;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            font-size: 26px;
                        `,
                        css`
                            font-size: 26px;
                        `,
                        css`
                            font-size: 30px;
                        `,
                        css`
                            font-size: 30px;
                        `,
                        css`
                            font-size: 30px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    categoryImageLink: {
        css: css`
            > span[class^="TypographyStyle"] {
                display: flex;
            }
        `,
    },
};
const styles = intPopularCategoriesStyles;

const IntPopularCategories: React.FC<Props> = props => {
    return (
        <StyledWrapper {...styles.popularCategoriescontainer}>
            <Typography {...styles.titleText}>{props.title}</Typography>
            <GridContainer {...styles.container}>
                {props.categoryLinks?.map(categorylink => (
                    <GridItem key={categorylink?.title.toString()} {...styles.categoryItem}>
                        <StyledWrapper {...styles.categoryWraper}>
                            {categorylink?.url && (
                                <Link href={categorylink?.url} {...styles.categoryImageLink}>
                                    <LazyImage src={categorylink?.smallImagePath} {...styles.categoryImage} />
                                </Link>
                            )}
                            <Link href={categorylink?.url} {...styles.categoryNameLink}>
                                <StyledWrapper {...styles.categoryNameContainer}>
                                    <Typography {...styles.categoryName}>{categorylink?.title}</Typography>
                                </StyledWrapper>
                            </Link>
                        </StyledWrapper>
                    </GridItem>
                ))}
            </GridContainer>
        </StyledWrapper>
    );
};

const popularCategoryList: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(IntPopularCategories),
    definition: {
        group: "Common",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                sortOrder: 0,
            },
            {
                name: fields.categories,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const { type, value } = item.fields.categoryLink;
                    const categoryState = getCategoryState(state, value);
                    if (!categoryState.value) {
                        if (!categoryState.isLoading && value) {
                            return dispatch => {
                                dispatch(
                                    loadCategory({
                                        id: value,
                                    }),
                                );
                            };
                        }
                        return "";
                    }
                    return categoryState.value.shortDescription;
                },
                defaultValue: [],
                fieldType: "Translatable",
                sortOrder: 1,
                fieldDefinitions: [
                    {
                        name: "categoryLink",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Category",
                        },
                        allowUrls: () => false,
                        isRequired: true,
                    },
                ],
            },
        ],
    },
};

export default popularCategoryList;
